/**
 * ConceptListModal — 개념 목록 창(설계 FE-20260913 §2-1 · 와이어프레임 W1-1).
 *
 * 오너 방침(2026-09-13): 「매스컨셉 리스트(현재 717개) 모달이 실행되면서 거기서 드래그하거나 선택해서 바로 추가」 ·
 * 「상세 검색도 가능하지만, 눈으로 보고 바로바로 선택」. 그래서 옛 개념 판(검색창 + 후보 50개, [적용] 전 임시 선택)을 걷고
 * 사전 전부를 첫 번째 태그로 묶은 격자(묶음 안은 순번 순)로 펼친다 — 학습 화면 개념 표와 같은 분류다(오너 2026-09-13).
 * 보기는 분류별(기본) · 순번순 · 가나다순 가운데 고른다 — 창을 열 때마다 분류별로 시작한다.
 * 고른 것은 **손을 뗀 순간** onChange 로 넘긴다(적용 단추 없음) — 부모가 이 행 초안에
 * 넣고, 검색은 작업 칸의 0.8초 자동 검색이 맡는다.
 *
 *   - 한 번 누르기 = 켜고 끄기. 누른 채 끌면(훑기) 지나간 칸을 누른 칸의 반대 상태로 맞추고, 손을 떼면 변경 한 번
 *   - 터치는 누른 칸이 포인터를 붙잡으므로 놓아 준다(그래야 옆 칸으로 훑인다). 목록을 굴려 취소되면(pointercancel) 누른 칸을 되돌린다
 *   - 찾기 · 영역 · 유형은 목록만 줄인다(고른 것은 그대로). 순번 범위로 채우기는 두지 않는다 — 따로 된 「개념 범위」 조건이
 *     맡는다(오너 2026-09-15 「범위 기능으로 별도로」). 연 항의 유래는 값을 건드리지 않았을 때만 남긴다
 *   - [되돌리기]는 창을 연 순간으로. Esc · [닫기] · 창 밖 누르기는 고른 상태 그대로 닫는다 — 즉시 반영이라 닫기가 취소가 아니다.
 *     창 밖 누르기는 누르는 순간(mousedown)만 본다 — 칸을 누른 채 창 밖까지 끌고 가 손을 떼는 것은 닫지 않는다(라벨 판과 같은 방식)
 *   - 키보드: 방향키로 칸 이동, Enter · Space(단추 누르기)로 켜고 끄기, Shift + 방향키로 훑기와 같게.
 *     칸은 단추라 키보드·보조기기의 누르기는 click 으로 온다 — 누르기(pointerdown)가 이미 켜고 끈 칸의 뒤따르는 click 만 삼킨다
 *   - 방식(하나라도·모두·정확히)은 이 창에 두지 않는다 — 빠른 조건의 매스컨셉 줄 끝에서 고친다(오너 2026-09-13).
 *     창은 값과 유래만 넘기고, 방식은 연 항의 것이 그대로 남는다
 * 넓은 화면(두 칸)은 avoidRef(결과 칸)의 왼쪽까지만 덮는다 — 결과를 보면서 고른다. 없거나 남는 폭이 좁으면 화면 전체.
 * 창 밖(body 의 다른 자식 — 앱 뿌리와 시험지 조립 같은 다른 창의 포털)을 inert 로 잠그고 초점을 창 안에 둔다. 결과 칸은 보이기만 한다.
 */
import {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, Search, X } from 'lucide-react';
import { getConceptTypeColor, renderConceptLatex } from '../common/ConceptChip';
import { MATH_TYPE_LABEL, CONCEPT_TYPE_LABEL } from '../common/report/reportColors';
import { LIMITS } from './syllabusQuery';
import {
  SORTS, conceptEntries, filterEntries, groupConcepts, resolveSource,
} from './conceptPick';

/** 결과 칸을 피해 왼쪽만 덮을 때 창이 가져야 할 최소 폭 — 이보다 좁으면 화면 전체로 연다 */
export const PANE_MIN_PX = 480;
const FOCUSABLE = 'input:not([disabled]),select:not([disabled]),button:not([disabled]),[tabindex]:not([tabindex="-1"])';
const AREAS = ['AL', 'AN', 'GE', 'PS'];
const ring = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const segCls = (on) => `rounded-full px-2.5 py-1 text-xs transition-colors ${ring} ${on ? 'bg-primary font-medium text-white' : 'text-gray-600 hover:bg-gray-200'}`;
const inputCls = 'rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 focus:border-primary focus:outline-none';
const linkCls = `inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 ${ring}`;
const BADGE = {
  borderRadius: '6px', width: '28px', height: '16px', minWidth: '28px',
};

const setOne = (list, id, on) => {
  const has = list.some((v) => String(v) === String(id));
  if (on && !has) return [...list, id];
  if (!on && has) return list.filter((v) => String(v) !== String(id));
  return list;
};
const sameList = (a, b) => a.length === b.length && a.every((v) => b.some((w) => String(w) === String(v)));

export default function ConceptListModal({
  term = null, dict = null, onChange, onClose, sheet = false, avoidRef = null, commonFrom = null,
}) {
  const [seed] = useState(() => ({ values: [...(term?.values || [])], term }));
  const [picked, setPicked] = useState(seed.values);
  const [q, setQ] = useState('');
  const [area, setArea] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState(SORTS[0].key); // 보기 — 분류별(기본) · 순번순 · 가나다순
  const [note, setNote] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [right, setRight] = useState(null);
  const layerRef = useRef(null);
  const paneRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const committedRef = useRef(seed.values); // 부모에게 마지막으로 넘긴 값 — 상한을 넘으면 여기로 되돌린다
  const sweepRef = useRef(null); // { on, list, before } — 누른 채 끄는 동안의 선택
  const pressedRef = useRef(null); // 누르기로 켜고 끈 칸 — 뒤따르는 click 을 한 번 삼킨다
  const keyMoveRef = useRef(false);
  const closeRef = useRef(onClose); // 부모가 매번 새로 만드는 닫기 — 창 밖 누르기 듣개는 한 번만 붙인다
  closeRef.current = onClose;

  const entries = useMemo(() => conceptEntries(dict), [dict]);
  const idOf = useMemo(() => new Map(entries.map((c) => [String(c.id), c.id])), [entries]);
  const liveCount = entries.filter((c) => !c.outdated).length;
  const visible = useMemo(() => filterEntries(entries, { q, area, type }), [entries, q, area, type]);
  const groups = useMemo(() => groupConcepts(visible, sort), [visible, sort]);
  const order = groups.flatMap((g) => g.items.map((c) => String(c.id)));
  const pickedSet = new Set(picked.map(String));

  /** 부모에게 넘긴다 — 값 · 유래(연 항의 유래는 최종 값이 그 범위와 같을 때만 남긴다, 옛 개념 판과 같은 규칙) */
  const commit = (values) => {
    if (values.length > LIMITS.concept_values) {
      setNote(`개념은 한 조건에 ${LIMITS.concept_values}개까지입니다.`);
      setPicked(committedRef.current);
      return;
    }
    setNote(null);
    committedRef.current = values;
    onChange({ values, source: resolveSource({ picked: values, dict, filled: null, term: seed.term }) });
  };

  // ── 창 밖 잠금 · 첫 초점 · 닫힐 때 연 요소로 초점 복귀(SyllabusTreeDrawer 와 같은 방식, 잠금 범위만 body 의 다른 자식 전부) ──
  useLayoutEffect(() => {
    const opener = document.activeElement;
    const layer = layerRef.current;
    const locked = Array.from(document.body.children)
      .filter((el) => el !== layer && !el.hasAttribute('inert'))
      .map((el) => ({ el, hidden: el.getAttribute('aria-hidden') }));
    locked.forEach(({ el }) => { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); });
    (searchRef.current || paneRef.current)?.focus();
    return () => {
      locked.forEach(({ el, hidden }) => {
        el.removeAttribute('inert');
        if (hidden == null) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', hidden);
      });
      setTimeout(() => {
        if (opener instanceof HTMLElement && opener !== document.body && document.contains(opener)) opener.focus();
      }, 0);
    };
  }, []);

  // ── 넓은 화면 — 결과 칸의 왼쪽까지만 덮는다(창과 결과 칸 사이 8px) ──
  useLayoutEffect(() => {
    const measure = () => {
      const r = sheet ? null : avoidRef?.current?.getBoundingClientRect();
      const roomy = r && r.width > 0 && r.left - 8 - 16 >= PANE_MIN_PX;
      setRight(roomy ? Math.max(0, window.innerWidth - r.left + 8) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [avoidRef, sheet]);

  // ── 훑기의 끝 — 창 밖에서 손을 떼도 한 번 반영한다. 터치 굴리기로 취소되면 누른 칸을 되돌린다 ──
  useEffect(() => {
    const end = (e) => {
      const s = sweepRef.current;
      if (!s) return;
      sweepRef.current = null;
      setTimeout(() => { pressedRef.current = null; }, 0);
      if (e.type === 'pointercancel') {
        setPicked(s.before);
        return;
      }
      commit(s.list);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  });

  // ── 창 밖 누르기 — 닫는다. 창 밖은 잠겨(inert) 눌린 대상이 body 쪽으로 잡히므로, 창 안이 아니면 모두 창 밖이다 ──
  useEffect(() => {
    const onDown = (e) => {
      const pane = paneRef.current;
      if (pane && e.target instanceof Node && !pane.contains(e.target)) closeRef.current('outside');
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // 키보드로 옮긴 칸에 초점을 준다(누르기로 옮긴 것은 누를 때 준다)
  useEffect(() => {
    if (!keyMoveRef.current || focusId == null) return;
    keyMoveRef.current = false;
    listRef.current?.querySelector(`[data-concept-id="${focusId}"]`)?.focus();
  }, [focusId]);

  const onPointerDown = (e, id) => {
    if (e.button != null && e.button !== 0) return;
    // 기본 동작(글자 선택 · 끌기 그림)을 막는다 — 대신 초점은 손으로 준다
    e.preventDefault();
    const el = e.currentTarget;
    if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
    el.focus({ preventScroll: true });
    const key = String(id);
    const before = pickedRef.current;
    const on = !before.some((v) => String(v) === key);
    const list = setOne(before, id, on);
    sweepRef.current = { on, list, before };
    pressedRef.current = key;
    setFocusId(key);
    setPicked(list);
  };
  const onPointerOver = (id) => {
    const s = sweepRef.current;
    if (!s) return;
    const list = setOne(s.list, id, s.on);
    if (list === s.list) return;
    s.list = list;
    setPicked(list);
  };

  const toggleKey = (key, on) => {
    const list = setOne(pickedRef.current, idOf.get(key) ?? key, on);
    setPicked(list);
    commit(list);
  };
  // 키보드 Enter · Space 와 보조기기의 누르기 — 누르기가 이미 켜고 끈 칸이면 삼킨다
  const onTileClick = (key) => {
    if (pressedRef.current === key) {
      pressedRef.current = null;
      return;
    }
    setFocusId(key);
    toggleKey(key, !pickedSet.has(key));
  };

  const onKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose('escape'); return; }
    if (e.key === 'Tab') {
      const pane = paneRef.current;
      const list = [...pane.querySelectorAll(FOCUSABLE)];
      const first = list[0];
      const last = list[list.length - 1];
      if (!first) { e.preventDefault(); pane.focus(); } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    const tile = e.target?.closest?.('[data-concept-id]');
    if (!tile) return;
    const key = tile.getAttribute('data-concept-id');
    const cols = Math.max(1, (getComputedStyle(tile.parentElement).gridTemplateColumns || '').split(' ').filter(Boolean).length);
    const step = {
      ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols,
    }[e.key];
    if (step == null) return;
    e.preventDefault();
    const i = order.indexOf(key);
    const next = order[Math.max(0, Math.min(order.length - 1, i + step))];
    if (next == null) return;
    if (e.shiftKey && next !== key) toggleKey(next, pickedSet.has(key));
    keyMoveRef.current = true;
    setFocusId(next);
  };

  const undo = () => {
    setPicked(seed.values);
    commit(seed.values);
  };
  const untouched = sameList(picked, seed.values);

  const groupCount = (g) => g.items.filter((c) => pickedSet.has(String(c.id))).length;
  // 태그 이름을 선택자에 넣지 않는다 — 따옴표 같은 글자가 섞여도 깨지지 않게 속성값을 그대로 비교한다
  const jumpTo = (key) => Array.from(listRef.current?.querySelectorAll('[data-group]') || [])
    .find((el) => el.getAttribute('data-group') === key)?.scrollIntoView?.({ block: 'start' });

  let paneStyle = {
    top: 16, right: 16, bottom: 16, left: 16,
  };
  if (sheet) {
    paneStyle = {
      top: 0, right: 0, bottom: 0, left: 0,
    };
  } else if (right != null) {
    paneStyle = {
      top: 16, right, bottom: 16, left: 16,
    };
  }
  const tabFor = (key) => (key === (focusId ?? order[0]) ? 0 : -1);

  return createPortal(
    <div ref={layerRef} className="pointer-events-none fixed inset-0" style={{ zIndex: 9999971 }}>
      <div
        ref={paneRef}
        role="dialog"
        aria-modal="true"
        aria-label="개념 목록"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={paneStyle}
        className={`pointer-events-auto absolute flex flex-col overflow-hidden bg-white shadow-2xl outline-none ${sheet ? '' : 'rounded-3xl border border-primary/30'}`}
      >
        <div className="shrink-0 space-y-2 border-b border-gray-100 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">매스컨셉 {liveCount}개</h3>
            <span role="status" aria-live="polite" className="text-xs text-gray-500">· 이 행에 고른 것 {picked.length}개</span>
            <span className="flex-1" />
            <button type="button" onClick={undo} disabled={untouched} className={linkCls}>
              <RotateCcw className="size-3.5" /> 되돌리기
            </button>
            <button type="button" onClick={() => onClose('button')} className={linkCls}>
              <X className="size-3.5" /> 닫기
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
              <input ref={searchRef} type="text" value={q} onChange={(e) => setQ(e.target.value)} aria-label="개념 찾기" placeholder="이름 · 순번 · 번호로 찾기" className={`${inputCls} w-48 pl-7`} />
            </label>
            <div role="group" aria-label="영역" className="inline-flex flex-wrap rounded-full bg-gray-100 p-0.5">
              <button type="button" aria-pressed={!area} onClick={() => setArea('')} className={segCls(!area)}>전체</button>
              {AREAS.map((a) => <button key={a} type="button" aria-pressed={area === a} onClick={() => setArea(a)} className={segCls(area === a)}>{MATH_TYPE_LABEL[a]}</button>)}
            </div>
            <select value={type} onChange={(e) => setType(e.target.value)} aria-label="개념 유형" className={inputCls}>
              <option value="">유형 전체</option>
              {Object.entries(CONCEPT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{`${k} ${v}`}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); if (listRef.current) listRef.current.scrollTop = 0; }}
              aria-label="보기"
              title="목록을 묶는 방법 — 기본은 분류별(첫 번째 태그, 매스컨셉 순서)"
              className={inputCls}
            >
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <span className="ml-auto text-[11px] text-gray-400">누르면 켜고 끄기 · 누른 채 끌면 여러 칸 · 손을 떼면 바로 반영</span>
          </div>
          {note && <p className="text-[11px] text-amber-700">{note}</p>}
        </div>

        <div className="flex min-h-0 flex-1">
          <nav aria-label={SORTS.find((s) => s.key === sort)?.nav} className="w-28 shrink-0 overflow-y-auto border-r border-gray-100 p-2">
            {groups.map((g) => (
              <button key={g.key} type="button" tabIndex={-1} onClick={() => jumpTo(g.key)} className="flex w-full items-center justify-between gap-1 rounded-lg px-2 py-1 text-left text-[11px] text-gray-600 hover:bg-gray-50">
                <span className="truncate" title={g.label}>{g.label}</span>
                {groupCount(g) > 0 && <span className="shrink-0 font-bold text-primary">{groupCount(g)}</span>}
              </button>
            ))}
          </nav>
          <div ref={listRef} className="min-h-0 flex-1 select-none overflow-y-auto p-3" style={{ touchAction: 'pan-y' }}>
            {groups.length === 0 && <p className="py-10 text-center text-sm text-gray-500">찾는 개념이 없습니다 — 찾기어를 줄이거나 영역을 「전체」로</p>}
            {groups.map((g) => (
              <section key={g.key} data-group={g.key} aria-label={g.label} className="mb-3">
                {/* 묶음 제목 — 칸 글자(text-xs)보다 크고 진하게(오너 2026-09-13 「더 크고 또렷하게」). 창 제목(굵게·900)보다는 한 단 아래 */}
                <h4 className="mb-2 text-sm font-semibold text-gray-800">{g.label}</h4>
                <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                  {g.items.map((c) => {
                    const key = String(c.id);
                    const on = pickedSet.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        data-concept-id={key}
                        aria-pressed={on}
                        tabIndex={tabFor(key)}
                        title={`${c.order ?? ''} ${c.name || `#${key}`}${c.outdated ? ' — 폐기된 개념' : ''}`}
                        onPointerDown={(e) => onPointerDown(e, c.id)}
                        onPointerOver={() => onPointerOver(c.id)}
                        onClick={() => onTileClick(key)}
                        className={`flex min-h-8 items-center gap-1 rounded-xl border px-1.5 py-1 text-left text-xs transition-colors ${ring} ${
                          on ? 'border-primary bg-primary/10 font-semibold text-gray-900' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        } ${c.outdated ? 'opacity-60' : ''}`}
                      >
                        {c.order != null && <span className="inline-flex items-center justify-center text-[9px] font-semibold text-gray-700" style={{ ...BADGE, backgroundColor: 'rgba(156, 163, 175, 0.3)' }}>{c.order}</span>}
                        {c.type && <span className="inline-flex items-center justify-center text-[8px] font-semibold text-gray-800" style={{ ...BADGE, backgroundColor: getConceptTypeColor(c.type) }}>{c.type}</span>}
                        {/* eslint-disable-next-line react/no-danger */}
                        <span className="min-w-0 flex-1 truncate" dangerouslySetInnerHTML={{ __html: renderConceptLatex(c.name || `#${key}`) }} />
                        {on && commonFrom && <span className="shrink-0 rounded-full bg-primary/10 px-1 text-[9px] font-bold text-primary" title={`상위 「${commonFrom}」의 공통 조건`}>공통</span>}
                        {c.outdated && <span className="shrink-0 text-[9px] text-gray-500">폐기</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
